import {createSlice, current, PayloadAction} from '@reduxjs/toolkit'
import {CustomFormFieldState} from '../../../components/FormControls/CustomFormField/CustomFormField'
import {FormStep, OrganismServiceOrderBookingFormProps, StepsState} from '../../../model/global'
import initialState from './initialState'
import {submitForm, validateSerialOrOrderNumber, validateStep} from './thunks'
import {AccordionArrayAction} from "../../../../../o-heating-form/react/model/global";


const stepsSlice = createSlice({
    name: 'stepsSlice',
    initialState,
    reducers: {
        initApp(state, {payload}: PayloadAction<OrganismServiceOrderBookingFormProps>) {
            // save step data in specified order
            state.steps = payload.steps
                // .filter(s => (s.stepName === 'step-5' || s.stepName === 'step-6'))
                .sort((a, b) => {
                    return a.stepOrder < b.stepOrder ? -1 : 1;
                }) as any[]
            state.endScreen = payload.endScreen
            state.activeStepIndex = 0
            state.formSubmitUrl = payload.formSubmitUrl;
        },

        setRequestedStep(state, {payload}: PayloadAction<StepsState['requestedStepIndex']>) {
            state.requestedStepIndex = payload
        },
        setActiveStep(state, {payload}: PayloadAction<StepsState['activeStepIndex']>) {
            // do not go to step if the form has been submitted
            const {formIsSubmitted} = state;
            if (formIsSubmitted) return;

            if (payload >= 0 && payload < state.steps.length) {
                state.activeStepIndex = payload
            }
        },
        setFormSubmitError(state, {payload}: PayloadAction<StepsState['formSubmitError']>) {
            state.formSubmitError = payload
        },
        /**
         * Initializes step payload data (using useEffect on step init)
         */
        initializeStepPayloadValues(state, {payload: {stepTemplate, stepPayload, initialize}}: PayloadAction<{
            stepTemplate: FormStep['stepTemplate'];
            stepPayload: Record<string, CustomFormFieldState<any>>;
            initialize: boolean;
        }>) {
            state.formSummary.steps[stepTemplate] = stepPayload as any;
            if (initialize) {
                state.formSummary.initialized[stepTemplate] = initialize
            }
        },
        updateAccordionArray(state, {payload: {stepPayload,action}}: PayloadAction<{
            stepPayload: any[],
            action: AccordionArrayAction
        }>) {
            state.steps[state.activeStepIndex].stepData.accordionArray = stepPayload;
            if (action !== AccordionArrayAction.OpenClose)
                state.formSummary.validatedSerialOrOrderNumber = false
            },
        /**
         * Update one or more form control values for a step
         * Accepts a single field or an array of fields
         * All form fields are flattened into a single object where the key name is the field name
         */
        updateStepPayloadValues(state, {payload: {stepTemplate, stepPayload, refreshSummary}}: PayloadAction<{
            stepTemplate: FormStep['stepTemplate'];
            stepPayload: CustomFormFieldState<any> | CustomFormFieldState<any>[],
            refreshSummary?: boolean;
        }>) {
            if (Array.isArray(stepPayload)) {

                // files fields are dynamic so need rebuilding each time!
                if (refreshSummary) {
                    state.formSummary.steps[stepTemplate] = {};
                }

                if (stepPayload.length === 0) {
                    state.formSummary.steps[stepTemplate] = {};
                } else {
                    stepPayload.forEach(step => {
                        if (state.formSummary.steps[stepTemplate][step.name]) {
                            state.formSummary.steps[stepTemplate][step.name] = step as any;
                        } else {
                            // @todo...
                            // file uploads uses this exception - because fields are dynamically built and do not exist in the JSON data!
                            state.formSummary.steps[stepTemplate][step.name] = step as any;
                        }
                    })

                }
            } else {
                if (state.formSummary.steps[stepTemplate][stepPayload.name]) {
                    state.formSummary.steps[stepTemplate][stepPayload.name] = stepPayload as any;
                }
            }

            // compute the validity of the overall step
            let stepIsValid = true;
            for (const [key, element] of Object.entries(state.formSummary.steps[stepTemplate])) {
                if (element?.valid === false) {
                    stepIsValid = false;
                }
            }
            // update validity of this step
            state.formSummary.validatedStepsHTML5[stepTemplate] = stepIsValid;
        },
        /** Allow external components to explicity declare a step as valid/invalid, depending on external logic */
        declareStepValidity(state, {payload: {stepTemplate, validity}}: PayloadAction<{
            stepTemplate: FormStep['stepTemplate'];
            validity: boolean;
        }>) {
            state.formSummary.validatedStepsHTML5[stepTemplate] = validity;
        },

    },
    extraReducers: (builder) => {
        builder.addCase(validateStep.fulfilled, (state, {payload}) => {
            const stepTemplate = state.steps[state.activeStepIndex].stepTemplate
            // check payload for success or otherwise
            if (payload && payload.success === true) {
                // update step validity
                //if there are no errors regarding serial or order number then remove all the messages
                if (state.formSummary.validatedSerialOrOrderNumber)
                    state.formSummary.validationErrors[stepTemplate] = {}
                state.formSummary.validatedSteps[stepTemplate] = true
            } else {
                // record errors for UI
                state.formSummary.validationErrors[stepTemplate] = {
                    ...state.formSummary.validationErrors[stepTemplate],
                    ...payload.error || {}
                }
                state.formSummary.validatedSteps[stepTemplate] = false
            }
        });
        builder.addCase(validateSerialOrOrderNumber.fulfilled, (state, {payload}) => {
            const stepTemplate = state.steps[state.activeStepIndex].stepTemplate
            // check payload for success or otherwise
            if (payload && payload.success) {
                // update step validity
                state.formSummary.validationErrors[stepTemplate] = {}
                state.formSummary.validatedSerialOrOrderNumber = true
            } else {
                // record errors for UI
                state.formSummary.validationErrors[stepTemplate] = payload?.error || {}
                state.formSummary.validatedSerialOrOrderNumber = false
            }
        });

        builder.addCase(submitForm.fulfilled, (state, {payload}) => {
            // check payload for success or otherwise
            if (payload && payload.success === true) {
                state.formSubmitError = '';
                // show welcome screen by toggling submitted state
                state.formIsSubmitted = true

            } else {
                state.formSubmitError = payload.error.toString();
            }
        });
        builder.addCase(submitForm.rejected, (state, {error}) => {
            if (error.message)
                state.formSubmitError = 'Something went wrong while sending a form!';
        })
    },
})

export const stepsActions = stepsSlice.actions
export default stepsSlice.reducer
